import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import ReactGA from "react-ga4";
import { setAuthenticatedUser } from "store/actions/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useFetchAssignments,
  useFetchMoodScales,
  useFetchPatients,
  useFetchTherapistProfile,
  useQueryParams,
  useSendCode,
  useFetchGroups,
  useFetchOrganizations,
  useFetchFiles,
  useCurrentUser,
  useFetchRoles,
} from "utils/hooks";

const useLogin = () => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const ref = useRef(false);
  const [userType, setUserType] = useState("");
  const { fetchAllPatients } = useFetchPatients();
  const { fetchTherapistProfile } = useFetchTherapistProfile();
  const { fetchAllAssignments } = useFetchAssignments();
  const { fetchAllMoodScales } = useFetchMoodScales();
  const { sendCode } = useSendCode();
  const { fetchGroups } = useFetchGroups();
  const { fetchOrganizations } = useFetchOrganizations();
  const { fetchTherapistFiles } = useFetchFiles();
  const { fetchRoles } = useFetchRoles();
  const { hasUrlParams } = useQueryParams();
  const { user } = useCurrentUser();
  const nextUrl = hasUrlParams("next");
  const qrTokenParam = hasUrlParams("qrToken");

  useEffect(() => {
    if (ref.current === true) return;
    if (userType === "therapist" && user) {
      fetchAllPatients();
      fetchTherapistProfile();
      fetchAllAssignments();
      fetchAllMoodScales();
      fetchGroups();
      fetchTherapistFiles();
      fetchRoles();
      if (user.role === "Owner") {
        fetchOrganizations();
      }
      ref.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submitLogin = async (args: {
    email: string;
    password: string;
    fromInvite?: string | null;
    inviteTherapistToken?: string | null;
    qrTokenParam?: string | null;
  }) => {
    if (!args) {
      return null;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
          query {
            login(
              email: "${args.email}",
              password: "${args.password}",
            ) {
              userId
              firstName
              lastName
              email
              expiresIn
              type
              verified
              lastLogin
              isSubscribed
              createdAt
              digestSubscription
              defaultLanguage
              is2FAEnabled
              slug
              defaultReminder
              notificationTimes
              daysOfWeek
              notificationMessage
              color
              icon
              alias
              activeOrganizationId
              activeOrganization {
                uuid
                name
                description
                slug
                subscription {
                  uuid
                  status
                  subId
                  plan {
                    uuid
                    name
                    slug
                    description
                    price_per_seat
                    seat_limit
                    practice_limit
                    group_limit
                    is_default
                    stripe_product_id
                    stripe_price_id
                    price_per_seat_annual
                    stripe_annual_price_id
                    is_public
                    features
                    patientLimit
                    assignmentLimit
                    customAssignmentLimit
                    customResourceLimit
                    fileLimit
                    groupLimit
                  }
                }
                createdAt
              }
              organizations {
                organizationId
                name
              }
              removedPracticeId
              pinCode
              pinVerified
              stripeCustomerId
              role
            }
          }
        `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: "NA",
          permissionKey: "login",
        },
      });

      const resData = await response.json();

      if (resData.errors && resData.errors[0].message === "GNRC_6016") {
        toast.error(
          `Please check your email to accept the invitation your therapist sent you.`
        );
        throw new Error("Failed!");
      }

      if (response.status !== 200 && response.status !== 201) {
        toast.error(`Hmm, something went wrong.`);
        throw new Error("Failed!");
      }

      const { login } = resData.data;

      if (login.is2FAEnabled) {
        await sendCode(login.email);
        let path = "/login/2fa";
        if (nextUrl) {
          path += `?next=${nextUrl}`;
        }
        if (qrTokenParam) {
          path += `?qrToken=${qrTokenParam}`;
        }
        return history(path, { state: login });
      } else {
        ReactGA.event("login_success", {
          user_id: login.userId,
          user_type: login.type,
          user_verified: login.verified,
        });
        const res = dispatch(setAuthenticatedUser(login));

        if (!login.verified) {
          const queryParams = new URLSearchParams();
          if (qrTokenParam) queryParams.append("qrToken", qrTokenParam);
          if (args.fromInvite)
            queryParams.append("fromInvite", args.fromInvite);
          if (args.inviteTherapistToken)
            queryParams.append(
              "inviteTherapistToken",
              args.inviteTherapistToken
            );

          return history(`/verification?${queryParams.toString()}`);
        }

        // fetching some data on login
        if (res.payload.type === "therapist") {
          setUserType("therapist");
        }

        if (res.payload.type === "patient") {
          fetchGroups();
          fetchOrganizations();
        }

        if (login.verified) {
          toast("Great to see you!");
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    submitLogin,
  };
};

export default useLogin;
